/**
 * Quem estava usando a tela quando o erro aconteceu, para dar para debugar no PostHog.
 *
 * Nome e e-mail vão inteiros; CPF/CNPJ vai meio mascarado (`555.***.***-08`), nunca completo.
 * Módulo puro (sem `import.meta` nem store) para ser testável no Jest: o store se registra com
 * `setErrorPersonProvider` em `src/store/index.ts`, o que evita import circular com o `reportError`.
 */

export interface ErrorPerson {
  name?: string;
  email?: string;
  document?: string;
}

type ErrorPersonProvider = () => ErrorPerson | null | undefined;

let provider: ErrorPersonProvider | null = null;

export function setErrorPersonProvider(next: ErrorPersonProvider | null): void {
  provider = next;
}

/** CPF: 3 primeiros e 2 últimos dígitos. CNPJ: 2 primeiros e 2 últimos. Outro formato: nada. */
export function maskDocument(value: unknown): string | undefined {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
  if (digits.length === 14) return `${digits.slice(0, 2)}.***.***/****-${digits.slice(12)}`;
  return undefined;
}

/** Mascara CPF/CNPJ (com ou sem pontuação) que aparecem no meio de rotas e mensagens. */
export function maskDocumentsInText(text: string): string {
  return text
    .replace(/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, (match) => maskDocument(match) ?? match)
    .replace(/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, (match) => maskDocument(match) ?? match);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function fromExtraData(extraData: Record<string, any>): ErrorPerson {
  return {
    name: text(extraData.name) ?? text(extraData.user?.name),
    email: text(extraData.email) ?? text(extraData.user?.email),
    document: text(extraData.document) ?? text(extraData.cpf) ?? text(extraData.user?.document)
  };
}

function fromProvider(): ErrorPerson {
  try {
    return provider?.() ?? {};
  } catch {
    return {};
  }
}

/** O que o call site mandou tem prioridade; o que faltar vem da pessoa do store. */
export function buildErrorPersonProps(extraData: Record<string, any> = {}): Record<string, string> {
  const own = fromExtraData(extraData ?? {});
  const stored = fromProvider();

  const props: Record<string, string | undefined> = {
    person_name: own.name ?? text(stored.name),
    person_email: own.email ?? text(stored.email),
    person_document: maskDocument(own.document ?? stored.document)
  };

  Object.keys(props).forEach((key) => props[key] === undefined && delete props[key]);
  return props as Record<string, string>;
}
